/**
 * Role Definitions and Capability Mappings
 * 
 * This file defines which capabilities each role has.
 * Changes here automatically propagate through the entire app via usePermissions hook.
 */

import type { Role, Capability, Permissions } from '@/lib/types'

/**
 * Capability mapping for each role
 * Add/remove capabilities here to change permissions globally
 */
export const ROLE_CAPABILITIES: Record<Role, Capability[]> = {
  admin: [
    // View & Reports
    'canView',

    // Entry Management (full)
    'canEditEntries',
    'canDeleteEntries',

    // Recurrence Management (full)
    'canCreateRecurrences',
    'canEditRecurrences',
    'canTerminateRecurrences',
    'canDeleteRecurrences',

    // Team & Settings
    'canManageTeam',
    'canManageSettings',

    // Billing (restricted to admin/owner)
    'canAccessBilling',
  ],

  manager: [
    // View & Reports
    'canView',

    // Entry Management (full)
    'canEditEntries',
    'canDeleteEntries',

    // Recurrence Management (full)
    'canCreateRecurrences',
    'canEditRecurrences',
    'canTerminateRecurrences',
    'canDeleteRecurrences',

    // Team & Settings
    'canManageTeam',
    'canManageSettings',

    // NO billing access for manager (only owner/admin)
    // 'canAccessBilling' - NOT for manager in v1
  ],

  operator: [
    // View & Reports
    'canView',

    // Entry Management (limited - no delete)
    'canEditEntries',
    // 'canDeleteEntries' - NOT for operator

    // Recurrence Management (operational only - no termination)
    'canCreateRecurrences',
    'canEditRecurrences',
    // 'canTerminateRecurrences' - NOT for operator
    // 'canDeleteRecurrences' - NOT for operator

    // NO team/settings/billing for operator
    // 'canManageTeam' - NOT for operator
    // 'canManageSettings' - NOT for operator
    // 'canAccessBilling' - NOT for operator
  ],

  viewer: [
    // View & Reports only
    'canView',

    // NO edit/delete/manage capabilities for viewer
  ],
}

/**
 * Get all capabilities for a role
 */
export function getRoleCapabilities(role: Role): Capability[] {
  return ROLE_CAPABILITIES[role] ?? []
}

/**
 * Check if a role has a specific capability
 */
export function roleHasCapability(role: Role, capability: Capability): boolean {
  return ROLE_CAPABILITIES[role]?.includes(capability) ?? false
}

/**
 * Role labels for UI display (i18n keys)
 */
export const ROLE_LABELS: Record<Role, string> = {
  admin: 'roles.admin',
  manager: 'roles.manager',
  operator: 'roles.operator',
  viewer: 'roles.viewer',
}

/**
 * Role descriptions for UI (i18n keys)
 */
export const ROLE_DESCRIPTIONS: Record<Role, string> = {
  admin: 'roles.adminDescription',
  manager: 'roles.managerDescription',
  operator: 'roles.operatorDescription',
  viewer: 'roles.viewerDescription',
}

/**
 * All available roles for dropdowns, etc
 */
export const ALL_ROLES: Role[] = ['admin', 'manager', 'operator', 'viewer']

/**
 * Roles that can invite others (have team management capability)
 */
export const ROLES_CAN_INVITE: Role[] = ['admin', 'manager']

/**
 * Roles that can access billing
 */
export const ROLES_CAN_ACCESS_BILLING: Role[] = ['admin']
